import { useState, useCallback, useMemo } from 'react'
import { Clock, CheckCircle, XCircle, AlertTriangle, Wallet, User, Flag, Loader2, ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { fadeInUp, staggerContainer, cardReveal, tapScale } from '@/lib/motion'
import { Drawer } from 'vaul'
import { useToast } from '@/components/shared/Toast'
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader'

type Tab = 'pending' | 'processed' | 'rejected'
type DrawerMode = { type: 'approve'; id: string } | { type: 'reject'; id: string } | null

interface PendingItem {
  id: string
  user: string
  avatar: string
  amount: number
  requestedAt: string
  wallet: string
  flagged: boolean
}

interface ProcessedItem {
  id: string
  user: string
  amount: number
  status: 'approved' | 'rejected'
  processedAt: string
}

const initialPending: PendingItem[] = [
  { id: 'W001', user: '小明', avatar: '🧑', amount: 50000, requestedAt: '5 分鐘前', wallet: '0x1a2b...9f8e', flagged: false },
  { id: 'W002', user: 'Lucky88', avatar: '🤠', amount: 150000, requestedAt: '23 分鐘前', wallet: '0x3c4d...7a6b', flagged: true },
  { id: 'W003', user: '阿花', avatar: '🌸', amount: 25000, requestedAt: '1 小時前', wallet: '0x5e6f...3c2d', flagged: false },
]

const initialProcessed: ProcessedItem[] = [
  { id: 'W100', user: 'Player_01', amount: 80000, status: 'approved', processedAt: '2 小時前' },
  { id: 'W101', user: '大衛', amount: 32000, status: 'approved', processedAt: '3 小時前' },
  { id: 'W102', user: 'AceKing', amount: 120000, status: 'rejected', processedAt: '4 小時前' },
  { id: 'W103', user: '小美', amount: 15000, status: 'approved', processedAt: '5 小時前' },
  { id: 'W104', user: 'PokerPro', amount: 200000, status: 'approved', processedAt: '6 小時前' },
  { id: 'W105', user: '阿強', amount: 45000, status: 'approved', processedAt: '8 小時前' },
  { id: 'W106', user: 'Tiger88', amount: 68000, status: 'approved', processedAt: '10 小時前' },
  { id: 'W107', user: '小龍', amount: 90000, status: 'approved', processedAt: '12 小時前' },
]

export function AdminWithdrawalsScreen() {
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<Tab>('pending')
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [pending, setPending] = useState<PendingItem[]>(initialPending)
  const [processed, setProcessed] = useState<ProcessedItem[]>(initialProcessed)
  const [isProcessing, setIsProcessing] = useState(false)
  const [tabDropdownOpen, setTabDropdownOpen] = useState(false)

  const activeItem = drawerMode
    ? pending.find((w) => w.id === drawerMode.id)
    : null

  const rejectedItems = processed.filter((w) => w.status === 'rejected')
  const approvedItems = processed.filter((w) => w.status === 'approved')
  const { headerRef, isMinimized } = useCollapsibleHeader()

  const totalPendingAmount = useMemo(
    () => pending.reduce((s, w) => s + w.amount, 0),
    [pending]
  )

  const handleConfirm = useCallback(() => {
    if (!drawerMode || !activeItem) return
    setIsProcessing(true)

    const action = drawerMode.type
    const item = activeItem

    setTimeout(() => {
      // Move from pending to processed
      setPending((prev) => prev.filter((w) => w.id !== item.id))
      setProcessed((prev) => [
        {
          id: item.id,
          user: item.user,
          amount: item.amount,
          status: action === 'approve' ? 'approved' : 'rejected',
          processedAt: '剛剛',
        },
        ...prev,
      ])

      if (action === 'approve') {
        toast({ type: 'success', title: '提款已批准', description: `$${(item.amount / 100).toFixed(2)} - ${item.user}` })
      } else {
        toast({ type: 'info', title: '提款已拒絕', description: `$${(item.amount / 100).toFixed(2)} - ${item.user}` })
      }

      setIsProcessing(false)
      setRejectReason('')
      setDrawerMode(null)
    }, 500)
  }, [drawerMode, activeItem, toast])

  const tabs: { id: Tab; label: string }[] = [
    { id: 'pending', label: '待審核' },
    { id: 'processed', label: '已處理' },
    { id: 'rejected', label: '已拒絕' },
  ]

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col min-h-full relative"
    >
      {/* Expanded Header — scrolls away naturally */}
      <motion.div ref={headerRef as React.RefObject<HTMLDivElement>} variants={fadeInUp} className="px-4 pt-4 pb-2 space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: '待審核', value: pending.length, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
            { label: '今日已處理', value: approvedItems.length, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
            { label: '已拒絕', value: rejectedItems.length, color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/20' },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              variants={cardReveal}
              className={cn('p-3 rounded-2xl glass-card border text-center', stat.bg)}
            >
              <p className={cn('text-2xl font-bold font-mono', stat.color)}>{stat.value}</p>
              <p className="text-[10px] text-muted-foreground font-medium mt-0.5">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Minimized sticky bar — overlays content, no layout space */}
      <div className="sticky top-0 z-40 h-0">
        <AnimatePresence>
          {isMinimized && (
            <motion.div
              initial={{ y: -44, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -44, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative bg-background/95 backdrop-blur-xl border-b border-foreground/5"
            >
              <div className="px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-amber-400">{pending.length} 待審</span>
                  <span className="text-xs font-bold font-mono text-gold">${(totalPendingAmount / 100).toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-emerald-400 font-bold">{approvedItems.length} 已處理</span>
                  <span className="text-[10px] text-rose-400 font-bold">{rejectedItems.length} 拒絕</span>
                  {/* Tab dropdown trigger */}
                  <button
                    onClick={() => setTabDropdownOpen(!tabDropdownOpen)}
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all border',
                      'bg-gold/20 text-gold border-gold/30'
                    )}
                  >
                    {tabs.find((t) => t.id === activeTab)?.label}
                    <ChevronDown className={cn('w-3 h-3 transition-transform', tabDropdownOpen && 'rotate-180')} />
                  </button>
                </div>
              </div>

              {/* Tab dropdown menu */}
              <AnimatePresence>
                {tabDropdownOpen && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-10"
                      onClick={() => setTabDropdownOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-4 top-full mt-1 z-20 min-w-[120px] p-1.5 rounded-xl bg-surface-elevated border border-foreground/10 shadow-xl"
                    >
                      {tabs.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => {
                            setActiveTab(tab.id)
                            setTabDropdownOpen(false)
                          }}
                          className={cn(
                            'w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-colors',
                            activeTab === tab.id
                              ? 'bg-gold/15 text-gold'
                              : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
                          )}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Tab Toggle */}
      <div className="px-4 pb-2">
        <div className="flex gap-1 p-1 rounded-xl bg-surface-elevated">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex-1 py-2 rounded-lg text-xs font-bold transition-all',
                activeTab === tab.id
                  ? 'bg-gold/20 text-gold border border-gold/30'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <motion.main variants={staggerContainer} className="px-4 pb-24 space-y-3 pt-2">
        {/* Pending Tab */}
        {activeTab === 'pending' &&
          (pending.length > 0
            ? pending.map((w) => (
                <motion.div
                  key={w.id}
                  variants={cardReveal}
                  onClick={() => setDrawerMode({ type: 'approve', id: w.id })}
                  className="p-4 rounded-2xl glass-card space-y-3 active:scale-[0.98] transition-transform cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-surface-elevated flex items-center justify-center text-lg">
                        {w.avatar}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-foreground">{w.user}</p>
                          {w.flagged && <Flag className="w-3.5 h-3.5 text-amber-400 fill-amber-400/30" />}
                        </div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {w.requestedAt}
                        </p>
                      </div>
                    </div>
                    <p className="text-xl font-bold font-mono text-gold">${(w.amount / 100).toFixed(2)}</p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface-elevated">
                      <Wallet className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs font-mono text-muted-foreground">{w.wallet}</span>
                    </div>
                    {w.flagged && (
                      <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
                        高額警示
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <motion.button
                      whileTap={tapScale}
                      onClick={(e) => { e.stopPropagation(); setDrawerMode({ type: 'approve', id: w.id }) }}
                      className="py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold flex items-center justify-center gap-1.5"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      批准
                    </motion.button>
                    <motion.button
                      whileTap={tapScale}
                      onClick={(e) => { e.stopPropagation(); setDrawerMode({ type: 'reject', id: w.id }) }}
                      className="py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold flex items-center justify-center gap-1.5"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      拒絕
                    </motion.button>
                  </div>
                </motion.div>
              ))
            : (
              <div className="text-center py-12">
                <CheckCircle className="w-10 h-10 text-emerald-400/40 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">所有提款已處理完畢</p>
              </div>
            ))}

        {/* Processed Tab */}
        {activeTab === 'processed' &&
          processed.map((w) => (
            <motion.div key={w.id} variants={cardReveal} className="flex items-center justify-between p-4 rounded-2xl glass-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-surface-elevated flex items-center justify-center">
                  <User className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">{w.user}</p>
                  <p className="text-xs text-muted-foreground">{w.processedAt}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-base font-bold font-mono text-foreground">${(w.amount / 100).toFixed(2)}</p>
                <span className={cn(
                  'text-[10px] font-bold px-2 py-0.5 rounded-full',
                  w.status === 'approved'
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                )}>
                  {w.status === 'approved' ? '已批准' : '已拒絕'}
                </span>
              </div>
            </motion.div>
          ))}

        {/* Rejected Tab */}
        {activeTab === 'rejected' &&
          (rejectedItems.length > 0
            ? rejectedItems.map((w) => (
                <motion.div key={w.id} variants={cardReveal} className="flex items-center justify-between p-4 rounded-2xl glass-card border border-rose-500/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-rose-500/10 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-rose-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">{w.user}</p>
                      <p className="text-xs text-muted-foreground">{w.processedAt}</p>
                    </div>
                  </div>
                  <p className="text-base font-bold font-mono text-rose-400">${(w.amount / 100).toFixed(2)}</p>
                </motion.div>
              ))
            : <p className="text-center text-sm text-muted-foreground py-12">無拒絕紀錄</p>)}
      </motion.main>

      {/* Approval / Rejection Drawer */}
      <Drawer.Root open={drawerMode !== null} onOpenChange={(open) => { if (!open) { setDrawerMode(null); setRejectReason(''); setIsProcessing(false) } }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[85vh] overflow-y-auto">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>
              <div className="px-6 pb-8 safe-area-bottom">
                <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-1">
                  {drawerMode?.type === 'approve' ? '批准提款' : '拒絕提款'}
                </Drawer.Title>
                <p className="text-sm text-muted-foreground mb-5">請確認提款詳情</p>

                {activeItem && (
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-elevated">
                      <div className="w-12 h-12 rounded-full bg-surface-elevated border border-foreground/10 flex items-center justify-center text-xl">
                        {activeItem.avatar}
                      </div>
                      <div>
                        <p className="text-base font-bold text-foreground">{activeItem.user}</p>
                        <p className="text-xs text-muted-foreground">ID: {activeItem.id}</p>
                      </div>
                    </div>

                    <div className="flex justify-between p-4 rounded-xl bg-surface-elevated">
                      <span className="text-muted-foreground">金額</span>
                      <span className="font-bold font-mono text-gold text-lg">${(activeItem.amount / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between p-4 rounded-xl bg-surface-elevated">
                      <span className="text-muted-foreground">錢包地址</span>
                      <span className="font-mono text-sm text-foreground">{activeItem.wallet}</span>
                    </div>
                    <div className="flex justify-between p-4 rounded-xl bg-surface-elevated">
                      <span className="text-muted-foreground">申請時間</span>
                      <span className="text-sm text-foreground">{activeItem.requestedAt}</span>
                    </div>

                    {activeItem.flagged && (
                      <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        <span className="text-xs text-amber-400 font-medium">此筆提款金額超過 $500，已標記為高額審核</span>
                      </div>
                    )}

                    {drawerMode?.type === 'reject' && (
                      <div className="space-y-2">
                        <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider">拒絕原因</label>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="請輸入拒絕原因..."
                          className="w-full h-24 p-3 rounded-xl bg-surface-elevated border border-foreground/10 text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:border-rose-500/40"
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => { setDrawerMode(null); setRejectReason(''); setIsProcessing(false) }}
                    disabled={isProcessing}
                    className="h-14 glass-card rounded-2xl font-semibold text-foreground disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={isProcessing}
                    className={cn(
                      'h-14 rounded-2xl font-bold text-white flex items-center justify-center gap-2',
                      drawerMode?.type === 'approve' ? 'bg-emerald-600' : 'bg-rose-600',
                      isProcessing && 'opacity-70'
                    )}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        處理中...
                      </>
                    ) : (
                      drawerMode?.type === 'approve' ? '確認批准' : '確認拒絕'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
